/** @name order_rows
 * This is the main file that provides the most essential data structure for the
 * list tables — the Order.
 *
 * The Order is an array of grouped columns. Such groups are used mostly by the table header,
 * so it can easily change the column width, their order, remove or add new columns,
 * box or unbox the group. The influence of the back-end for this Order is minimal.
 */

/**
 * This is a detailed list of columns that are used in the list (main) table
 */
import ORDER_MAIN from './fields-main';

import t from '@/i18n';

/**
 * This is a detailed list of groups that are used in the list (main) table
 */
const GROUPS_MAIN = [
  {
    name: '',
    color: '',
    extendable: false,
    fields: [
      'item_no',
      'description_of_works',
      'custom',
      'unit',
      'quantity',
      'comments',
      'contractor',
    ],
  },
  {
    name: t('Per unit'),
    extendable: true,
    fields: ['price_per_hour', 'working_hours', 'labour', 'materials', 'tools', 'total'],
  },
  {
    name: t('Total cost'),
    extendable: true,
    fields: ['labour_total', 'materials_total', 'tools_total', 'total_total'],
  },
];

/**
 * A structure that defines different word-explained widths in their pixel-fraction representation
 */
export const COLUMN_WIDTHS = {
  min: { pixels: 16, fractions: 'auto' },
  tiny: { pixels: 120, fractions: '1fr' },
  narrow: { pixels: 200, fractions: '1fr' },
  wide: { pixels: 250, fractions: '2fr' },
};

export const COLUMN_WIDTH_TO_GRID = (pixels) => {
  if (pixels > 200) {
    return { pixels: pixels || 250, fractions: '1fr' };
  } else {
    return { pixels: pixels || 250, fractions: '2fr' };
  }
};

/**
 * This function unites the back-end order with the front-end one.
 * Basically, it merges ORDER_MAIN into the GROUPS_MAIN by field name, and checks
 * if they are used in the proper order with back-end.
 *
 * @param fields {main: {}, per_value: {}} – the fields object from back-end
 * @param columnsWidth
 * @returns {{groups: *}[]}
 */
export const fieldsOrder = (fields = {}, columnsWidth = {}) => {
  // Proxy weird [{fieldKey: fieldName}] array from back-end to {fieldKey:fieldName, ...}
  const main = {};
  fields = Object.values(fields).flat();
  fields.forEach((field) => Object.keys(field).forEach((key) => (main[key] = field[key])));

  const order = ORDER_MAIN;
  const groups = GROUPS_MAIN;

  // Find keys that are not defined to have some logic (custom fields)
  const customKeys = Object.keys(main).filter((key) => !order.some((field) => field.slug === key));

  // Collect all names of fields together with the information about them in right grouped order
  return groups.map((group) => ({
    ...group,
    fields: group.fields
      .map((data) => {
        const field = order.find((order) => order.slug === data);
        if (!field) return null;

        const width = COLUMN_WIDTHS[field.width];

        // Slug==='custom' means 'Get all fields that are not in our predefined list.
        if (field.slug === 'custom') {
          return customKeys.map((key, i) => ({
            ...field,
            slug: key,
            name: main[key] || `Untitled ${i}`,
            width: COLUMN_WIDTH_TO_GRID(columnsWidth[key]),
            custom: true,
          }));

          // Required fields means that they would be merged into the Order only if present in back-end order
        } else if (!field.required || main[field.slug]) {
          return {
            ...field,
            slug: field.slug,
            // TODO: decomment the line when you want the imported names to be used instead of pre-defined ones
            // name: main[field.slug] || field.name,
            name: field.name,
            width,
            custom: false,
          };
        } else return null;
      })
      .flat()
      .filter((data) => !!data),
  }));
};

/**
 * Function finds the field in the fieldsOrder by the unique slug
 * @param order {{groups: *}[]} – array of groups of fields
 * @param slug {string}
 */
export const findField = (order, slug) => {
  return order.map((group) => group.fields.filter((field) => field.slug === slug)).flat()[0];
};
